import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const DEFAULT_CURRENCY = 'INR';
const RATES_BASE = 'USD';
const STORAGE_KEY = 'homevilas.currency';
const RATES_KEY = 'homevilas.currencyRates';
const RATES_TS_KEY = 'homevilas.currencyRatesTs';
const REFRESH_MS = 10 * 60 * 1000;
const MAX_AGE_MS = 60 * 60 * 1000;

const SUPPORTED_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'AUD', 'CAD', 'SGD'];

const CurrencyContext = createContext(null);

const normalize = (value) => String(value || '').trim().toUpperCase();

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatNumber = (value, currency, options = {}) => {
  const num = safeNumber(value);
  if (num === null) return null;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    }).format(num);
  } catch (_) {
    return `${currency} ${num.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    })}`;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_CURRENCY;
    const stored = normalize(window.localStorage.getItem(STORAGE_KEY));
    return SUPPORTED_CURRENCIES.includes(stored) ? stored : DEFAULT_CURRENCY;
  });
  const [rates, setRates] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = JSON.parse(window.localStorage.getItem(RATES_KEY) || '{}');
      return stored && typeof stored === 'object' ? stored : {};
    } catch (_) {
      return {};
    }
  });
  const [lastUpdated, setLastUpdated] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const stored = Number(window.localStorage.getItem(RATES_TS_KEY));
    return Number.isFinite(stored) ? stored : 0;
  });
  const [loading, setLoading] = useState(false);

  const persistRates = useCallback((nextRates, ts) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(RATES_KEY, JSON.stringify(nextRates));
    window.localStorage.setItem(RATES_TS_KEY, String(ts));
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`https://open.er-api.com/v6/latest/${RATES_BASE}`);
      if (!res.ok) throw new Error('Rates request failed');
      const payload = await res.json();
      const nextRates = payload?.rates || {};
      if (nextRates && typeof nextRates === 'object') {
        const stamped = Date.now();
        setRates(nextRates);
        setLastUpdated(stamped);
        persistRates(nextRates, stamped);
      }
    } catch (_) {
      // keep cached rates if fetch fails
    } finally {
      setLoading(false);
    }
  }, [persistRates]);

  useEffect(() => {
    const stale = !lastUpdated || Date.now() - lastUpdated > MAX_AGE_MS;
    if (stale) fetchRates();
    const interval = setInterval(fetchRates, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchRates, lastUpdated]);

  const updateCurrency = useCallback((next) => {
    const normalized = normalize(next) || DEFAULT_CURRENCY;
    const safeValue = SUPPORTED_CURRENCIES.includes(normalized) ? normalized : DEFAULT_CURRENCY;
    setCurrency(safeValue);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, safeValue);
    }
  }, []);

  const convertAmount = useCallback(
    (value, fromCurrency, toCurrency) => {
      const amount = safeNumber(value);
      if (amount === null) return null;
      const from = normalize(fromCurrency) || DEFAULT_CURRENCY;
      const to = normalize(toCurrency) || currency || DEFAULT_CURRENCY;
      if (from === to) return amount;
      const fromRate = rates?.[from];
      const toRate = rates?.[to];
      if (!fromRate || !toRate) return amount;
      return (amount / fromRate) * toRate;
    },
    [currency, rates]
  );

  const formatCurrency = useCallback(
    (value, fromCurrency, options = {}) => {
      const converted = convertAmount(value, fromCurrency, currency);
      return formatNumber(converted, currency, options);
    },
    [convertAmount, currency]
  );

  const value = useMemo(
    () => ({
      currency,
      supportedCurrencies: SUPPORTED_CURRENCIES,
      loading,
      lastUpdated,
      setCurrency: updateCurrency,
      convertAmount,
      formatCurrency,
    }),
    [convertAmount, currency, formatCurrency, lastUpdated, loading, updateCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
